import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  OnInit,
  Inject,
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAuditOperation, SnCamera, SnContentType } from '@lib/api/entities';
import { SnAuditService } from '@lib/api/services';
import { PursuitService } from '@lib/cameras/services/pursuit.service';
import { ISnPlayer } from '@lib/player/common';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';

import { SnVideoPlayerComponent } from '../../video-player.component';

@Component({
  selector: 'sn-video-player-screenshot',
  templateUrl: './video-player-screenshot.control.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sn-player__screenshot-control',
  },
})
export class SnVideoPlayerScreenshotControl implements OnInit, OnDestroy {
  @Input() public camera: SnCamera;
  @Input() public alwaysOn = false;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject('SN_PLAYER') private readonly _player: SnVideoPlayerComponent,
    private readonly _pursuitService: PursuitService,
    private readonly _toastrService: ToastrService,
    private readonly _auditService: SnAuditService,
    private readonly _matDialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    this._pursuitService
      .getScreenshot()
      .pipe(takeUntil(this._destroy$))
      .subscribe((value: boolean) => {
        if (value) {
          this.snapAi();
        }
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get player(): ISnPlayer {
    return this._player;
  }

  public snapAi(): void {
    this._toastrService.overlayContainer = this._player.toastContainer;
    this._player.isClipping = true;
    const video = this._player.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    canvas.toBlob((blob) => {
      const now = new Date();
      const name = `${now.getTime()}`;
      const file: any = blob;
      file.lastModifiedDate = new Date();
      file.name = `snapshot-${name}.jpeg`;
      const dataSnap = {
        file,
        camera: this.camera,
      };
      this._pursuitService.setSnapAi(dataSnap);
    });
  }

  public clip(): void {
    const payload: any = {
      object: {
        id: this.camera.id,
        name: this.camera.name,
        contentType: SnContentType.Camera,
      },
      operation: SnAuditOperation.VideoScreenShot,
      changeData: {
        screenShotVideoTime: this._player.timeStamp,
      },
    };
    this._auditService
      .createAudit([payload])
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});

    this._toastrService.overlayContainer = this._player.toastContainer;
    this._player.isClipping = true;
    const video = this._player.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const dialogRef = this._matDialog.open(SnShareImageDialogComponent, {
      data: {
        url: canvas.toDataURL(),
        camera: this.camera,
        playerRef: this._player,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => (this._player.isClipping = false));
  }
}
